import { Controller, useFormContext } from 'react-hook-form'

import { CustomFieldRestriction } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import {
  FieldHeader,
  OptionProps,
  SelectField as SelectFieldComponent,
} from '@avantstay/backoffice-core'

import { TaxonomyTask } from './CaseTaxonomy.types'

/**
 * Status select for new-taxonomy cases: options come from the selected
 * task's status set. Taxonomy statuses have no SFDC api name, so the label
 * doubles as the form's `status` value (keeping the cancellation checks and
 * the record inputs' statusApiName working) while `taxonomyStatusId` carries
 * the id the update mutation sends — the backend derives the legacy status
 * from it.
 *
 * FOPS and work-order forms seed `status` with the label string; the other
 * record-type forms seed a `{ label, value }` option and read `status.value`
 * on save, so the field accepts both and keeps whichever shape it was given.
 */
interface TaxonomyStatusSelectFieldProps {
  formName?: string
}

export function TaxonomyStatusSelectField({ formName = 'status' }: TaxonomyStatusSelectFieldProps) {
  const { control, watch, setValue } = useFormContext()
  const selectedTask = watch('taxonomyTask') as TaxonomyTask | null | undefined

  const options: OptionProps<string>[] = (selectedTask?.statuses ?? []).map(status => ({
    label: status.label,
    value: status.label,
  }))

  return (
    <FieldHeader.Default
      title="Status"
      restriction={CustomFieldRestriction.required}
      marginBottom={24}
    >
      <Controller
        control={control}
        name={formName}
        rules={{ required: 'Status field is required' }}
        render={({ field: { value }, fieldState }) => {
          const isOptionValue = typeof value === 'object' && value !== null
          const selectedLabel = isOptionValue
            ? (value as OptionProps<string>).value
            : (value as string | null | undefined)
          const selectedOption = selectedLabel
            ? (options.find(option => option.value === selectedLabel) ?? {
                label: selectedLabel,
                value: selectedLabel,
              })
            : null

          return (
            <SelectFieldComponent.Default
              options={options}
              placeholder="Select..."
              error={fieldState?.error?.message}
              isDisabled={!options.length}
              onChange={(selected: OptionProps<string>) => {
                setValue(formName, isOptionValue ? selected : selected.value)
                setValue(
                  'taxonomyStatusId',
                  selectedTask?.statuses.find(status => status.label === selected.value)?.statusId,
                )
              }}
              value={selectedOption}
            />
          )
        }}
      />
    </FieldHeader.Default>
  )
}
